// Create a new file: /components/workflow/ToolInputNodeModal.jsx

import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Edit } from 'lucide-react';
import Modal from "../base/Modal"; // Assuming a base modal component exists
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import AutoGrowTextarea from '../AutoGrowTextarea';
import { schemaToFields, fieldsToSchema } from '../NewWorkflow/utils/schemaUtils';

// const schemaToFields = (schema) => {
//   if (!schema || !schema.properties) return [];

//   const convertProperties = (properties, requiredFields = []) => {
//     return Object.entries(properties).map(([key, value]) => {
//       const field = {
//         id: `field_${Math.random().toString(36).substring(2, 9)}`,
//         fieldName: key,
//         dataType: value.type.charAt(0).toUpperCase() + value.type.slice(1), // Capitalize e.g., 'string' -> 'String'
//         required: requiredFields.includes(key),
//         description: value.description || '',
//         children: [],
//         arrayItemType: 'String', // Default
//       };

//       if (value.type === 'object' && value.properties) {
//         field.children = convertProperties(value.properties, value.required || []);
//       } else if (value.type === 'array' && value.items) {
//         field.arrayItemType = value.items.type.charAt(0).toUpperCase() + value.items.type.slice(1);
//         if (value.items.type === 'object' && value.items.properties) {
//           field.children = convertProperties(value.items.properties, value.items.required || []);
//         }
//       }
//       return field;
//     });
//   };

//   return convertProperties(schema.properties, schema.required || []);
// };

// const fieldsToSchema = (fields, modelName = 'Output') => {
//     const convert = (fieldList) => {
//         const properties = {};
//         const required = [];

//         fieldList.forEach(field => {
//             if (!field.fieldName) return;
//             let schemaDef = { description: field.description };

//             const dataType = field.dataType.toLowerCase();

//             switch (dataType) {
//                 case 'string': case 'number': case 'boolean':
//                     schemaDef.type = dataType;
//                     break;
//                 case 'object':
//                     schemaDef.type = 'object';
//                     const nested = convert(field.children || []);
//                     schemaDef.properties = nested.properties;
//                     if (nested.required.length > 0) schemaDef.required = nested.required;
//                     break;
//                 case 'array':
//                     schemaDef.type = 'array';
//                     const itemType = field.arrayItemType?.toLowerCase() || 'string';
//                     if (itemType === 'object') {
//                         const nestedArray = convert(field.children || []);
//                         schemaDef.items = {
//                             type: 'object',
//                             properties: nestedArray.properties,
//                         };
//                         if (nestedArray.required.length > 0) {
//                             schemaDef.items.required = nestedArray.required;
//                         }
//                     } else {
//                         schemaDef.items = { type: itemType };
//                     }
//                     break;
//                 default:
//                     schemaDef.type = 'string';
//             }
//             properties[field.fieldName] = schemaDef;
//             if (field.required) required.push(field.fieldName);
//         });
//         return { properties, required };
//     };

//     const root = convert(fields);
//     return {
//         title: modelName,
//         type: 'object',
//         properties: root.properties,
//         ...(root.required.length > 0 && { required: root.required }),
//     };
// };

const ToolInputNodeModal = ({ mode, nodeData, onSave, onDelete, onClose, globalState }) => {
  // --- NEW STATE FOR TOOL DETAILS ---
  const [toolName, setToolName] = useState(nodeData?.data?.toolName || '');
  const [toolDescription, setToolDescription] = useState(nodeData?.data?.toolDescription || '');

  const [outputSchema, setOutputSchema] = useState(nodeData?.data?.jsonOutputSchema || { title: 'Input', type: 'object', properties: {} });
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  const initialSchemaFields = useMemo(() => schemaToFields(outputSchema), [outputSchema]);
  const propertyCount = Object.keys(outputSchema?.properties || {}).length;

  const handleSchemaSave = ({ fields, modelName }) => {
    const newSchema = fieldsToSchema(fields, modelName);
    setOutputSchema(newSchema);
    setIsBuilderOpen(false);
    toast.success("Input schema updated!");
  };

  const handleSave = () => {
    // --- VALIDATION ---
    if (!toolName.trim()) {
      toast.error("Tool Name is required.");
      return;
    }
    const variableNameRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    if (!variableNameRegex.test(toolName)) {
      toast.error("Tool Name must be a valid variable name (e.g., camelCase or snake_case).");
      return;
    }
    if (!toolDescription.trim()) {
      toast.error("Tool Description is required.");
      return;
    }

    onSave({
      ...nodeData,
      data: {
        // label: `Input (${propertyCount} fields)`,
        // --- UPDATED DATA PAYLOAD ---
        label: toolName, // Use the tool name as the primary label
        toolName,
        toolDescription,
        jsonOutput: true, // This node always has a structured output
        jsonOutputSchema: outputSchema,
      },
    });
  };

  return (
    <>
      <Modal title="Configure Tool Input Node" isOpen={true} onClose={onClose}>
        <div className="p-6 bg-zinc-900 rounded-lg">
          <p className="text-zinc-300 mb-4">
            Define the name, description, and data structure (schema) for your tool. This information will be used to execute the tool correctly.
          </p>
          {/* --- NEW TOOL DETAILS SECTION --- */}
          <div className="space-y-4 mb-6">
            <div>
                <label className="block text-sm font-medium text-zinc-200 mb-2">
                    Tool Name
                </label>
                <input
                    type="text"
                    placeholder="e.g., createJiraTicket or create_jira_ticket"
                    value={toolName}
                    onChange={(e) => setToolName(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                />
                 <p className="text-xs text-zinc-500 mt-1">Must be a valid variable name (camelCase or snake_case).</p>
            </div>
            <div>
                 <label className="block text-sm font-medium text-zinc-200 mb-2">
                    Tool Description
                </label>
                <AutoGrowTextarea
                    placeholder="Enter a clear and concise description of what this tool does."
                    value={toolDescription}
                    onChange={(e) => setToolDescription(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                />
            </div>
          </div>
          <div className="p-4 bg-zinc-800 rounded-md">
            <h4 className="font-semibold text-zinc-100">Output Schema</h4>
            <p className="text-sm text-zinc-400 mt-1 mb-3">
              {propertyCount > 0 ? `Schema defined with ${propertyCount} top-level fields.` : "No schema defined yet."}
            </p>
            <button
              onClick={() => setIsBuilderOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <Edit size={16} />
              {propertyCount > 0 ? "Edit Schema" : "Create Schema"}
            </button>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            {mode === "edit" && (
              <button
                className="px-4 py-2 text-zinc-300 bg-red-800 hover:bg-red-700 border border-red-700 rounded-md"
                onClick={() => onDelete(nodeData.id)}
              >
                Delete Node
              </button>
            )}
            <button
              className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              onClick={handleSave}
            >
              {mode === "add" ? "Add Node" : "Save Changes"}
            </button>
          </div>
        </div>
      </Modal>

      <SchemaBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSchemaSave}
        initialFields={initialSchemaFields}
        initialModelName={outputSchema?.title || 'ToolInput'}
      />
    </>
  );
};

// Paste the schemaToFields and fieldsToSchema functions here

export default ToolInputNodeModal;